import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, Search, X, RefreshCw,
  FileText, Calendar, User, DollarSign, TrendingUp
} from 'lucide-react';
import CustomerInvoiceForm from './CustomerInvoiceForm';
import apiClient from '../services/api';
import '../styles/CustomerInvoice.css';

const CustomerInvoice = () => {
  const [invoices, setInvoices]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [showForm, setShowForm]           = useState(false);
  const [editInvoice, setEditInvoice]     = useState(null);   // null = new, obj = edit
  const [searchTerm, setSearchTerm]       = useState('');
  const [statusFilter, setStatusFilter]   = useState('All');

  /* ── session ── */
  const getSession = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };
  const session = getSession();

  useEffect(() => { loadInvoices(); }, []);

  /* ── load list ── */
  const loadInvoices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/CustomerInvoices.php');
      if (res.data.success) {
        setInvoices(res.data.data || []);
      } else {
        throw new Error(res.data.message || 'Failed to load invoices');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to load invoices');
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── open form ── */
  const handleNew  = ()       => { setEditInvoice(null); setShowForm(true); };
  const handleEdit = (inv)    => { setEditInvoice(inv);  setShowForm(true); };
  const handleClose = ()      => { setShowForm(false); setEditInvoice(null); };

  /* ── delete ── */
  const handleDelete = async (id, invoiceNo) => {
    if (!window.confirm(`Delete invoice ${invoiceNo}? This cannot be undone.`)) return;
    try {
      const res = await apiClient.delete('/CustomerInvoices.php', {
        params: { id }
      });
      if (res.data.success) {
        loadInvoices();
      } else {
        alert('Error: ' + res.data.message);
      }
    } catch (err) {
      alert('Error deleting: ' + (err.response?.data?.message || err.message));
    }
  };

  /* ── after form save ── */
  const handleSaved = () => {
    setShowForm(false);
    setEditInvoice(null);
    loadInvoices();
  };

  /* ── filter ── */
  const statuses = ['All', 'Draft', 'Sent', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'];

  const filtered = invoices.filter(inv => {
    const matchSearch =
      (inv.InvoiceNo       || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.customername    || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.ReferenceNo     || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'All' || inv.Status === statusFilter;
    return matchSearch && matchStatus;
  });

  /* ── status badge colour ── */
  const statusClass = (s) => {
    switch ((s || '').toLowerCase()) {
      case 'paid':           return 'ci-badge-paid';
      case 'sent':           return 'ci-badge-sent';
      case 'overdue':        return 'ci-badge-overdue';
      case 'cancelled':      return 'ci-badge-cancelled';
      case 'partially paid': return 'ci-badge-partial';
      default:               return 'ci-badge-draft';
    }
  };

  /* ── summary totals ── */
  const totalAmount  = filtered.reduce((s, i) => s + parseFloat(i.TotalAmount  || 0), 0);
  const totalBalance = filtered.reduce((s, i) => s + parseFloat(i.BalanceAmount || 0), 0);

  if (!session?.companyid) {
    return (
      <div className="ci-page">
        <div className="ci-no-session">
          <FileText size={40} />
          <h3>No Session</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ci-page">

      {/* ════════ PAGE HEADER ════════ */}
      <div className="ci-page-header">
        <div className="ci-page-header-left">
          <div className="ci-page-icon">
            <FileText size={26} color="#fff" />
          </div>
          <div>
            <h1 className="ci-page-title">Customer Invoices</h1>
            <p className="ci-page-subtitle">Revenue invoices &amp; billing management</p>
          </div>
        </div>
        <div className="ci-page-header-actions">
          <button className="ci-btn-refresh" onClick={loadInvoices} title="Refresh" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'ci-spin' : ''} />
          </button>
          <button className="ci-btn-new" onClick={handleNew}>
            <Plus size={18} />
            New Invoice
          </button>
        </div>
      </div>

      {/* ════════ SUMMARY CARDS ════════ */}
      <div className="ci-summary-cards">
        <div className="ci-card">
          <div className="ci-card-icon ci-card-icon-blue"><FileText size={20} /></div>
          <div>
            <div className="ci-card-value">{filtered.length}</div>
            <div className="ci-card-label">Total Invoices</div>
          </div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon ci-card-icon-green"><TrendingUp size={20} /></div>
          <div>
            <div className="ci-card-value">{totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="ci-card-label">Total Invoiced</div>
          </div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon ci-card-icon-orange"><DollarSign size={20} /></div>
          <div>
            <div className="ci-card-value">{totalBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="ci-card-label">Outstanding Balance</div>
          </div>
        </div>
        <div className="ci-card">
          <div className="ci-card-icon ci-card-icon-red"><Calendar size={20} /></div>
          <div>
            <div className="ci-card-value">{invoices.filter(i => i.Status === 'Overdue').length}</div>
            <div className="ci-card-label">Overdue</div>
          </div>
        </div>
      </div>

      {/* ════════ TOOLBAR ════════ */}
      <div className="ci-toolbar">
        <div className="ci-search-wrap">
          <Search size={16} className="ci-search-icon" />
          <input
            className="ci-search-input"
            type="text"
            placeholder="Search invoice no, customer, reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="ci-search-clear" onClick={() => setSearchTerm('')}><X size={14} /></button>
          )}
        </div>
        <div className="ci-status-tabs">
          {statuses.map(s => (
            <button
              key={s}
              className={`ci-status-tab ${statusFilter === s ? 'ci-status-tab-active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ci-result-count">
          {filtered.length} {filtered.length === 1 ? 'invoice' : 'invoices'}
        </div>
      </div>

      {/* ════════ CONTENT ════════ */}
      {loading ? (
        <div className="ci-loading">
          <div className="ci-spinner" />
          <span>Loading invoices...</span>
        </div>
      ) : error ? (
        <div className="ci-error">
          <strong>Error:</strong> {error}
          <button className="ci-btn-new" style={{ marginLeft: 16 }} onClick={loadInvoices}>Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="ci-empty">
          <FileText size={52} style={{ opacity: 0.2, marginBottom: 12 }} />
          <h3>{searchTerm || statusFilter !== 'All' ? 'No matching invoices' : 'No invoices yet'}</h3>
          <p>
            {searchTerm || statusFilter !== 'All'
              ? 'Try adjusting your search or filter'
              : 'Create your first customer invoice to get started'}
          </p>
          {!searchTerm && statusFilter === 'All' && (
            <button className="ci-btn-new" style={{ marginTop: 16 }} onClick={handleNew}>
              <Plus size={18} /> New Invoice
            </button>
          )}
        </div>
      ) : (
        <div className="ci-table-wrap">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Invoice No</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Customer</th>
                <th>Currency</th>
                <th className="ci-th-right">Sub Total</th>
                <th className="ci-th-right">Tax</th>
                <th className="ci-th-right">Total Amount</th>
                <th className="ci-th-right">Balance</th>
                <th>Status</th>
                <th className="ci-th-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.InvoiceID} className="ci-tr">
                  <td>
                    <span
                      className="ci-invoice-link"
                      onClick={() => handleEdit(inv)}
                      title="Click to edit"
                    >
                      {inv.InvoiceNo}
                    </span>
                  </td>
                  <td>
                    <div className="ci-date-cell">
                      <Calendar size={12} />
                      {inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString() : '—'}
                    </div>
                  </td>
                  <td className={inv.Status !== 'Paid' && inv.DueDate && new Date(inv.DueDate) < new Date() ? 'ci-overdue-date' : ''}>
                    {inv.DueDate ? new Date(inv.DueDate).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="ci-customer-cell">
                      <User size={12} />
                      <div>
                        <div className="ci-customer-name">{inv.customername || '—'}</div>
                        {inv.customercode && <div className="ci-customer-code">{inv.customercode}</div>}
                      </div>
                    </div>
                  </td>
                  <td>{inv.currencycode || '—'}</td>
                  <td className="ci-td-right ci-mono">{parseFloat(inv.SubTotal    || 0).toFixed(2)}</td>
                  <td className="ci-td-right ci-mono">{parseFloat(inv.TaxAmount   || 0).toFixed(2)}</td>
                  <td className="ci-td-right ci-mono ci-amount-bold">
                    <div className="ci-amount-cell">
                      <DollarSign size={11} />
                      {parseFloat(inv.TotalAmount  || 0).toFixed(2)}
                    </div>
                  </td>
                  <td className="ci-td-right ci-mono">{parseFloat(inv.BalanceAmount || 0).toFixed(2)}</td>
                  <td>
                    <span className={`ci-badge ${statusClass(inv.Status)}`}>
                      {inv.Status || 'Draft'}
                    </span>
                  </td>
                  <td className="ci-td-center">
                    <div className="ci-row-actions">
                      <button
                        className="ci-action-btn ci-action-edit"
                        onClick={() => handleEdit(inv)}
                        title="Edit Invoice"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="ci-action-btn ci-action-delete"
                        onClick={() => handleDelete(inv.InvoiceID, inv.InvoiceNo)}
                        title="Delete Invoice"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ════════ FORM MODAL ════════ */}
      {showForm && (
        <CustomerInvoiceForm
          editData={editInvoice}
          onClose={handleClose}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default CustomerInvoice;